import React, { useState, useEffect } from 'react';
import { theme } from '../../styles/theme';
import { discogsService, DiscogsConfig } from '../../services/discogsService';
import { secureSupabaseService } from '../../services/secureSupabaseService';

interface SyncStatusProps {
  albumCount: number;
  onClose: () => void;
  onStartSync: () => void;
  onStartArtistEnhancement?: () => void;
  onStartITunesEnhancement?: () => void;
}

const SyncStatus: React.FC<SyncStatusProps> = ({ albumCount, onClose, onStartSync, onStartArtistEnhancement, onStartITunesEnhancement }) => {
  const [discogsConfig, setDiscogsConfig] = useState<DiscogsConfig>({
    username: '',
    token: '',
    baseUrl: 'https://api.discogs.com'
  });
  const [testingConnection, setTestingConnection] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState<string>('');
  const [showDiscogsConfig, setShowDiscogsConfig] = useState(false);
  const overlayStyle: React.CSSProperties = {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: theme.zIndex.modal,
  };

  const modalStyle: React.CSSProperties = {
    backgroundColor: theme.colors.surface,
    borderRadius: theme.borderRadius.lg,
    padding: theme.spacing.xl,
    maxWidth: '500px',
    border: `1px solid ${theme.colors.border}`,
  };

  const headerStyle: React.CSSProperties = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing.lg,
  };

  const titleStyle: React.CSSProperties = {
    fontSize: '1.5rem',
    fontWeight: '700',
    color: theme.colors.text,
  };

  const closeButtonStyle: React.CSSProperties = {
    background: 'none',
    border: 'none',
    fontSize: '1.5rem',
    color: theme.colors.text,
    cursor: 'pointer',
    padding: theme.spacing.sm,
  };

  const statusItemStyle: React.CSSProperties = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: theme.spacing.md,
    backgroundColor: theme.colors.surfaceLight,
    borderRadius: theme.borderRadius.md,
    marginBottom: theme.spacing.sm,
  };

  const statusReadyStyle: React.CSSProperties = {
    color: theme.colors.success,
    fontWeight: '500',
  };

  const statusPendingStyle: React.CSSProperties = {
    color: theme.colors.textSecondary,
    fontStyle: 'italic',
  };

  const buttonStyle: React.CSSProperties = {
    backgroundColor: theme.colors.primary,
    color: 'white',
    border: 'none',
    padding: `${theme.spacing.sm} ${theme.spacing.md}`,
    borderRadius: theme.borderRadius.md,
    fontSize: '0.875rem',
    cursor: 'pointer',
  };

  const inputStyle: React.CSSProperties = {
    width: '100%',
    padding: theme.spacing.sm,
    borderRadius: theme.borderRadius.sm,
    border: `1px solid ${theme.colors.border}`,
    backgroundColor: theme.colors.background,
    color: theme.colors.text,
    fontSize: '0.875rem',
    marginTop: '4px',
  };

  const handleTestConnection = async () => {
    setTestingConnection(true);
    setConnectionStatus('');
    
    try {
      discogsService.setConfig(discogsConfig);
      const result = await discogsService.testConnection();
      setConnectionStatus(result.success ? '✅ Connected successfully!' : `❌ ${result.message}`);
    } catch (error: any) {
      setConnectionStatus(`❌ ${error.message}`);
    } finally {
      setTestingConnection(false);
    }
  };

  const isDiscogsConfigured = discogsService.isConfigured();

  // Load Discogs config on component mount
  useEffect(() => {
    const loadDiscogsConfig = async () => {
      try {
        const settings = await secureSupabaseService.getSettings();
        if (settings.discogs_username && settings.discogs_token) {
          const savedConfig: DiscogsConfig = {
            username: settings.discogs_username,
            token: settings.discogs_token,
            baseUrl: 'https://api.discogs.com'
          };
          setDiscogsConfig(savedConfig);
          discogsService.setConfig(savedConfig);
        }
      } catch (error) {
        console.log('Could not load Discogs configuration:', error);
      }
    };
    
    loadDiscogsConfig();
  }, []);

  const handleSaveDiscogsConfig = async () => {
    try {
      await secureSupabaseService.updateSettings({
        discogs_username: discogsConfig.username,
        discogs_token: discogsConfig.token
      });
      discogsService.setConfig(discogsConfig);
      alert('✅ Discogs configuration saved successfully!');
    } catch (error) {
      console.error('Failed to save Discogs configuration:', error);
      alert(`❌ Failed to save: ${error}`);
    }
  };

  return (
    <div style={overlayStyle} onClick={onClose}>
      <div style={modalStyle} onClick={(e) => e.stopPropagation()}>
        <div style={headerStyle}>
          <h2 style={titleStyle}>Data Sync Status</h2>
          <button style={closeButtonStyle} onClick={onClose}>×</button>
        </div>

        <p style={{ 
          color: theme.colors.textSecondary, 
          marginBottom: theme.spacing.lg,
          lineHeight: 1.5 
        }}>
          Ready to enhance your collection with data from external sources.
        </p>

        <div style={statusItemStyle}>
          <span>Local Collection</span>
          <span style={statusReadyStyle}>{albumCount} albums ready</span>
        </div>

        <div style={statusItemStyle}>
          <span>Data Storage</span>
          <div style={{ display: 'flex', alignItems: 'center', gap: theme.spacing.sm }}>
            <span style={statusReadyStyle}>IndexedDB ready</span>
          </div>
        </div>

        <div style={statusItemStyle}>
          <span>Kallax Configuration</span>
          <span style={statusReadyStyle}>Settings persisted</span>
        </div>

        <div style={statusItemStyle}>
          <span>Discogs Integration</span>
          <div style={{ display: 'flex', alignItems: 'center', gap: theme.spacing.sm }}>
            {isDiscogsConfigured ? (
              <>
                <span style={statusReadyStyle}>Ready</span>
                <button 
                  style={{...buttonStyle, backgroundColor: theme.colors.success}} 
                  onClick={onStartSync}
                >
                  Sync Collection
                </button>
              </>
            ) : (
              <span style={statusPendingStyle}>Not configured</span>
            )}
            <button 
              style={buttonStyle} 
              onClick={() => setShowDiscogsConfig(!showDiscogsConfig)}
            >
              {showDiscogsConfig ? 'Hide' : 'Configure'}
            </button>
          </div>
        </div>

        {showDiscogsConfig && (
          <div style={{
            padding: theme.spacing.lg,
            backgroundColor: theme.colors.background,
            borderRadius: theme.borderRadius.md,
            marginBottom: theme.spacing.sm,
          }}>
            <h3 style={{ 
              margin: `0 0 ${theme.spacing.md} 0`,
              color: theme.colors.text,
              fontSize: '1rem',
            }}>
              Discogs API Configuration
            </h3>
            
            <div style={{ marginBottom: theme.spacing.md }}>
              <label style={{ 
                fontSize: '0.875rem', 
                color: theme.colors.text,
                fontWeight: '500',
              }}>
                Username
              </label>
              <input
                type="text"
                value={discogsConfig.username}
                onChange={(e) => setDiscogsConfig({ ...discogsConfig, username: e.target.value })}
                style={inputStyle}
                placeholder="Your Discogs username"
              />
            </div>

            <div style={{ marginBottom: theme.spacing.md }}>
              <label style={{ 
                fontSize: '0.875rem', 
                color: theme.colors.text,
                fontWeight: '500',
              }}>
                Personal Access Token
              </label>
              <input
                type="password"
                value={discogsConfig.token}
                onChange={(e) => setDiscogsConfig({ ...discogsConfig, token: e.target.value })}
                style={inputStyle}
                placeholder="Generate token at discogs.com/settings/developers"
              />
            </div>

            <div style={{ display: 'flex', gap: theme.spacing.sm, alignItems: 'center' }}>
              <button 
                style={buttonStyle} 
                onClick={handleTestConnection}
                disabled={testingConnection || !discogsConfig.username || !discogsConfig.token}
              >
                {testingConnection ? 'Testing...' : 'Test Connection'}
              </button>
              
              <button 
                style={{...buttonStyle, backgroundColor: theme.colors.success}} 
                onClick={handleSaveDiscogsConfig}
                disabled={!discogsConfig.username || !discogsConfig.token}
              >
                Save Config
              </button>
              
              {connectionStatus && (
                <span style={{ 
                  fontSize: '0.875rem',
                  color: connectionStatus.includes('✅') ? theme.colors.success : theme.colors.error,
                }}>
                  {connectionStatus}
                </span>
              )}
            </div>

            <div style={{
              marginTop: theme.spacing.md,
              padding: theme.spacing.sm,
              backgroundColor: `${theme.colors.primary}10`,
              borderRadius: theme.borderRadius.sm,
              fontSize: '0.75rem',
              color: theme.colors.textSecondary,
              lineHeight: 1.4,
            }}>
              <strong>How to get a token:</strong><br/>
              1. Go to discogs.com/settings/developers<br/>
              2. Click "Generate new token"<br/>
              3. Copy the token and paste it above
            </div>
          </div>
        )}


        <div style={statusItemStyle}>
          <span>Artist Sorting Enhancement</span>
          <div style={{ display: 'flex', alignItems: 'center', gap: theme.spacing.sm }}>
            {albumCount > 0 ? (
              <>
                <span style={statusReadyStyle}>Ready</span>
                <button 
                  style={{...buttonStyle, backgroundColor: theme.colors.accent}} 
                  onClick={onStartArtistEnhancement}
                >
                  Enhance Sorting
                </button>
              </>
            ) : (
              <span style={statusPendingStyle}>No albums to enhance</span>
            )}
          </div>
        </div>

        <div style={statusItemStyle}>
          <span>iTunes Audio Previews</span>
          <div style={{ display: 'flex', alignItems: 'center', gap: theme.spacing.sm }}>
            {albumCount > 0 ? (
              <>
                <span style={statusReadyStyle}>Ready</span>
                <button 
                  style={{...buttonStyle, backgroundColor: '#007AFF'}} // iTunes blue
                  onClick={onStartITunesEnhancement}
                  disabled={!onStartITunesEnhancement}
                >
                  Add Previews
                </button>
              </>
            ) : (
              <span style={statusPendingStyle}>No albums to enhance</span>
            )}
          </div>
        </div>


        <div style={{
          marginTop: theme.spacing.lg,
          padding: theme.spacing.md,
          backgroundColor: `${theme.colors.primary}20`,
          borderRadius: theme.borderRadius.md,
          color: theme.colors.textSecondary,
          fontSize: '0.875rem',
          lineHeight: 1.5,
        }}>
          <strong>Artist Sorting:</strong> Use MusicBrainz to correctly identify individual artists vs bands, 
          fixing cases where band names are incorrectly sorted by last name.
        </div>
      </div>
    </div>
  );
};

export default SyncStatus;